export interface ChannelData {
  name: string
  value: number
  color: string
}

export interface CampaignMetrics {
  budget: string
  impressions: string
  clicks: string
  leads: string
  timeOnSite: string
  bounceRate: string
  costPerLead: string
  rawBudget: number
  rawImpressions: number
  rawClicks: number
  rawLeads: number
}

export interface Campaign {
  id: string
  title: string
  brand: 'Toyota' | 'Lexus'
  type: 'Brand' | 'Tactical'
  date: string
  year: string
  market: string
  model: string
  metrics: CampaignMetrics
  channels: ChannelData[]
}

export const brandModels: Record<string, string[]> = {
  Toyota: ['C-HR', 'Yaris', 'Yaris Cross', 'RAV4', 'Corolla'],
  Lexus: ['LBX', 'UX', 'UX EV', 'NX', 'RZ', 'RX'],
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) return `€${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `€${(value / 1000).toFixed(0)}K`
  return `€${value}`
}

const formatNumber = (value: number) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
  return value.toString()
}

const generateMetrics = (
  budget: number,
  type: 'Brand' | 'Tactical',
): CampaignMetrics => {
  const cpc = type === 'Brand' ? 0.8 : 0.4
  const cpl = type === 'Brand' ? 150 : 45
  const ctr = type === 'Brand' ? 0.015 : 0.035
  const clicks = Math.floor(budget / cpc)
  const impressions = Math.floor(clicks / ctr)
  const leads = Math.floor(budget / cpl)
  const bounceRates =
    type === 'Brand'
      ? ['38%', '42%', '45%', '48%']
      : ['52%', '58%', '62%', '65%']
  const times =
    type === 'Brand'
      ? ['2m 10s', '2m 30s', '2m 45s', '3m 05s']
      : ['1m 05s', '1m 15s', '1m 30s', '1m 45s']
  const i = Math.floor(Math.random() * 4)
  return {
    budget: formatCurrency(budget),
    impressions: formatNumber(impressions),
    clicks: formatNumber(clicks),
    leads: formatNumber(leads),
    timeOnSite: times[i],
    bounceRate: bounceRates[i],
    costPerLead: `€${cpl}`,
    rawBudget: budget,
    rawImpressions: impressions,
    rawClicks: clicks,
    rawLeads: leads,
  }
}

export const allCampaigns: Campaign[] = [
  // =====================
  // TOYOTA — C-HR
  // =====================
  {
    id: 't-chr-1',
    title: 'C-HR New Year Launch',
    brand: 'Toyota',
    type: 'Brand',
    date: 'Jan – Mar 2024',
    year: '2024',
    market: 'SLO',
    model: 'C-HR',
    metrics: generateMetrics(140000, 'Brand'),
    channels: [
      { name: 'YouTube', value: 45, color: '#ef4444' },
      { name: 'Meta', value: 35, color: '#3b82f6' },
      { name: 'Display', value: 20, color: '#f59e0b' },
    ],
  },
  {
    id: 't-chr-2',
    title: 'C-HR Spring Tactical',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Apr – Jun 2024',
    year: '2024',
    market: 'SLO',
    model: 'C-HR',
    metrics: generateMetrics(75000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 55, color: '#10b981' },
      { name: 'Meta', value: 35, color: '#3b82f6' },
      { name: 'Email', value: 10, color: '#8b5cf6' },
    ],
  },
  {
    id: 't-chr-3',
    title: 'C-HR Summer Drive',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Jul – Sep 2024',
    year: '2024',
    market: 'CRO',
    model: 'C-HR',
    metrics: generateMetrics(65000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 60, color: '#10b981' },
      { name: 'Meta', value: 40, color: '#3b82f6' },
    ],
  },
  {
    id: 't-chr-4',
    title: 'C-HR Design Focus',
    brand: 'Toyota',
    type: 'Brand',
    date: 'Jan – Apr 2025',
    year: '2025',
    market: 'BiH',
    model: 'C-HR',
    metrics: generateMetrics(180000, 'Brand'),
    channels: [
      { name: 'Instagram', value: 60, color: '#ec4899' },
      { name: 'YouTube', value: 25, color: '#ef4444' },
      { name: 'Display', value: 15, color: '#f59e0b' },
    ],
  },
  {
    id: 't-chr-5',
    title: 'C-HR Hybrid Tech',
    brand: 'Toyota',
    type: 'Brand',
    date: 'Jan – Mar 2026',
    year: '2026',
    market: 'SLO',
    model: 'C-HR',
    metrics: generateMetrics(190000, 'Brand'),
    channels: [
      { name: 'YouTube', value: 45, color: '#ef4444' },
      { name: 'Meta', value: 35, color: '#3b82f6' },
      { name: 'Display', value: 20, color: '#f59e0b' },
    ],
  },
  {
    id: 't-chr-6',
    title: 'C-HR Autumn Deals',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Sep – Nov 2025',
    year: '2025',
    market: 'SLO',
    model: 'C-HR',
    metrics: generateMetrics(70000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 50, color: '#10b981' },
      { name: 'Meta', value: 40, color: '#3b82f6' },
      { name: 'Email', value: 10, color: '#8b5cf6' },
    ],
  },

  // =====================
  // TOYOTA — Yaris
  // =====================
  {
    id: 't-yar-1',
    title: 'Yaris City Drive',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Mar – Jun 2025',
    year: '2025',
    market: 'MNE',
    model: 'Yaris',
    metrics: generateMetrics(60000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 50, color: '#10b981' },
      { name: 'Meta', value: 40, color: '#3b82f6' },
      { name: 'TikTok', value: 10, color: '#000000' },
    ],
  },
  {
    id: 't-yar-2',
    title: 'Yaris Back to School',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Aug – Oct 2024',
    year: '2024',
    market: 'BiH',
    model: 'Yaris',
    metrics: generateMetrics(55000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 50, color: '#10b981' },
      { name: 'Meta', value: 40, color: '#3b82f6' },
      { name: 'TikTok', value: 10, color: '#000000' },
    ],
  },
  {
    id: 't-yar-3',
    title: 'Yaris Urban Style',
    brand: 'Toyota',
    type: 'Brand',
    date: 'Jan – Mar 2024',
    year: '2024',
    market: 'SLO',
    model: 'Yaris',
    metrics: generateMetrics(110000, 'Brand'),
    channels: [
      { name: 'Meta', value: 50, color: '#3b82f6' },
      { name: 'YouTube', value: 30, color: '#ef4444' },
      { name: 'Display', value: 20, color: '#f59e0b' },
    ],
  },
  {
    id: 't-yar-4',
    title: 'Yaris Spring Promo',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Apr – Jun 2024',
    year: '2024',
    market: 'SLO',
    model: 'Yaris',
    metrics: generateMetrics(48000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 65, color: '#10b981' },
      { name: 'Meta', value: 35, color: '#3b82f6' },
    ],
  },

  // =====================
  // TOYOTA — Yaris Cross
  // =====================
  {
    id: 't-yc-1',
    title: 'Yaris Cross Launch',
    brand: 'Toyota',
    type: 'Brand',
    date: 'Jan – Mar 2024',
    year: '2024',
    market: 'SLO',
    model: 'Yaris Cross',
    metrics: generateMetrics(150000, 'Brand'),
    channels: [
      { name: 'Meta', value: 45, color: '#3b82f6' },
      { name: 'Google Ads', value: 35, color: '#10b981' },
      { name: 'Display', value: 20, color: '#f59e0b' },
    ],
  },
  {
    id: 't-yc-2',
    title: 'Yaris Cross Spring Sales',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Apr – Jun 2024',
    year: '2024',
    market: 'CRO',
    model: 'Yaris Cross',
    metrics: generateMetrics(85000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 60, color: '#10b981' },
      { name: 'Meta', value: 30, color: '#3b82f6' },
      { name: 'Email', value: 10, color: '#8b5cf6' },
    ],
  },
  {
    id: 't-yc-3',
    title: 'Yaris Cross Summer Adventure',
    brand: 'Toyota',
    type: 'Brand',
    date: 'Jun – Aug 2024',
    year: '2024',
    market: 'SLO',
    model: 'Yaris Cross',
    metrics: generateMetrics(130000, 'Brand'),
    channels: [
      { name: 'YouTube', value: 40, color: '#ef4444' },
      { name: 'Meta', value: 40, color: '#3b82f6' },
      { name: 'Display', value: 20, color: '#f59e0b' },
    ],
  },
  {
    id: 't-yc-4',
    title: 'Yaris Cross End of Year',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Oct – Dec 2024',
    year: '2024',
    market: 'RS',
    model: 'Yaris Cross',
    metrics: generateMetrics(90000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 50, color: '#10b981' },
      { name: 'Meta', value: 40, color: '#3b82f6' },
      { name: 'Email', value: 10, color: '#8b5cf6' },
    ],
  },
  {
    id: 't-yc-5',
    title: 'Yaris Cross 2025 Awareness',
    brand: 'Toyota',
    type: 'Brand',
    date: 'Feb – May 2025',
    year: '2025',
    market: 'SLO',
    model: 'Yaris Cross',
    metrics: generateMetrics(170000, 'Brand'),
    channels: [
      { name: 'Meta', value: 40, color: '#3b82f6' },
      { name: 'YouTube', value: 35, color: '#ef4444' },
      { name: 'Instagram', value: 25, color: '#ec4899' },
    ],
  },
  {
    id: 't-yc-6',
    title: 'Yaris Cross Summer 2026',
    brand: 'Toyota',
    type: 'Brand',
    date: 'May – Aug 2026',
    year: '2026',
    market: 'RS',
    model: 'Yaris Cross',
    metrics: generateMetrics(160000, 'Brand'),
    channels: [
      { name: 'Meta', value: 50, color: '#3b82f6' },
      { name: 'YouTube', value: 30, color: '#ef4444' },
      { name: 'Display', value: 20, color: '#f59e0b' },
    ],
  },

  // =====================
  // TOYOTA — RAV4
  // =====================
  {
    id: 't-rav-1',
    title: 'RAV4 Hybrid Awareness',
    brand: 'Toyota',
    type: 'Brand',
    date: 'May – Sep 2023',
    year: '2023',
    market: 'RS',
    model: 'RAV4',
    metrics: generateMetrics(200000, 'Brand'),
    channels: [
      { name: 'YouTube', value: 50, color: '#ef4444' },
      { name: 'Display', value: 30, color: '#f59e0b' },
      { name: 'Meta', value: 20, color: '#3b82f6' },
    ],
  },
  {
    id: 't-rav-2',
    title: 'RAV4 Winter Promo',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Oct – Dec 2024',
    year: '2024',
    market: 'SLO',
    model: 'RAV4',
    metrics: generateMetrics(95000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 45, color: '#10b981' },
      { name: 'Meta', value: 45, color: '#3b82f6' },
      { name: 'Email', value: 10, color: '#8b5cf6' },
    ],
  },
  {
    id: 't-rav-3',
    title: 'RAV4 Spring Offer',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Mar – May 2024',
    year: '2024',
    market: 'CRO',
    model: 'RAV4',
    metrics: generateMetrics(80000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 55, color: '#10b981' },
      { name: 'Meta', value: 35, color: '#3b82f6' },
      { name: 'Email', value: 10, color: '#8b5cf6' },
    ],
  },
  {
    id: 't-rav-4',
    title: 'RAV4 Family Adventure',
    brand: 'Toyota',
    type: 'Brand',
    date: 'Jun – Sep 2026',
    year: '2026',
    market: 'CRO',
    model: 'RAV4',
    metrics: generateMetrics(210000, 'Brand'),
    channels: [
      { name: 'Meta', value: 40, color: '#3b82f6' },
      { name: 'YouTube', value: 40, color: '#ef4444' },
      { name: 'Display', value: 20, color: '#f59e0b' },
    ],
  },
  {
    id: 't-rav-5',
    title: 'RAV4 Adventure Days',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Jan – Mar 2025',
    year: '2025',
    market: 'MNE',
    model: 'RAV4',
    metrics: generateMetrics(72000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 60, color: '#10b981' },
      { name: 'Meta', value: 30, color: '#3b82f6' },
      { name: 'Local PR', value: 10, color: '#64748b' },
    ],
  },

  // =====================
  // TOYOTA — Corolla
  // =====================
  {
    id: 't-cor-1',
    title: 'Corolla End of Year',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Oct – Dec 2023',
    year: '2023',
    market: 'SLO',
    model: 'Corolla',
    metrics: generateMetrics(120000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 55, color: '#10b981' },
      { name: 'Meta', value: 35, color: '#3b82f6' },
      { name: 'Local PR', value: 10, color: '#64748b' },
    ],
  },
  {
    id: 't-cor-2',
    title: 'Corolla Fleet Push',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Jun – Sep 2025',
    year: '2025',
    market: 'CRO',
    model: 'Corolla',
    metrics: generateMetrics(110000, 'Tactical'),
    channels: [
      { name: 'LinkedIn', value: 70, color: '#0ea5e9' },
      { name: 'Google Ads', value: 20, color: '#10b981' },
      { name: 'Email', value: 10, color: '#8b5cf6' },
    ],
  },
  {
    id: 't-cor-3',
    title: 'Corolla Sedan Awareness',
    brand: 'Toyota',
    type: 'Brand',
    date: 'Jan – Apr 2024',
    year: '2024',
    market: 'SLO',
    model: 'Corolla',
    metrics: generateMetrics(135000, 'Brand'),
    channels: [
      { name: 'Meta', value: 45, color: '#3b82f6' },
      { name: 'YouTube', value: 35, color: '#ef4444' },
      { name: 'Display', value: 20, color: '#f59e0b' },
    ],
  },
  {
    id: 't-cor-4',
    title: 'Corolla Summer Deals',
    brand: 'Toyota',
    type: 'Tactical',
    date: 'Jun – Aug 2024',
    year: '2024',
    market: 'RS',
    model: 'Corolla',
    metrics: generateMetrics(68000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 60, color: '#10b981' },
      { name: 'Meta', value: 40, color: '#3b82f6' },
    ],
  },

  // =====================
  // LEXUS — LBX
  // =====================
  {
    id: 'l-lbx-1',
    title: 'LBX Premiere',
    brand: 'Lexus',
    type: 'Brand',
    date: 'Jan – Apr 2024',
    year: '2024',
    market: 'SLO',
    model: 'LBX',
    metrics: generateMetrics(250000, 'Brand'),
    channels: [
      { name: 'YouTube', value: 40, color: '#ef4444' },
      { name: 'Meta', value: 30, color: '#3b82f6' },
      { name: 'Premium Display', value: 30, color: '#8b5cf6' },
    ],
  },
  {
    id: 'l-lbx-2',
    title: 'LBX Summer Experience',
    brand: 'Lexus',
    type: 'Tactical',
    date: 'May – Aug 2024',
    year: '2024',
    market: 'SLO',
    model: 'LBX',
    metrics: generateMetrics(120000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 50, color: '#10b981' },
      { name: 'Meta', value: 40, color: '#3b82f6' },
      { name: 'Email', value: 10, color: '#8b5cf6' },
    ],
  },
  {
    id: 'l-lbx-3',
    title: 'LBX Urban Style',
    brand: 'Lexus',
    type: 'Tactical',
    date: 'Mar – Jun 2025',
    year: '2025',
    market: 'CRO',
    model: 'LBX',
    metrics: generateMetrics(140000, 'Tactical'),
    channels: [
      { name: 'Meta', value: 50, color: '#3b82f6' },
      { name: 'Google Ads', value: 40, color: '#10b981' },
      { name: 'TikTok', value: 10, color: '#000000' },
    ],
  },
  {
    id: 'l-lbx-4',
    title: 'LBX Winter Campaign',
    brand: 'Lexus',
    type: 'Brand',
    date: 'Oct – Dec 2025',
    year: '2025',
    market: 'SLO',
    model: 'LBX',
    metrics: generateMetrics(200000, 'Brand'),
    channels: [
      { name: 'YouTube', value: 45, color: '#ef4444' },
      { name: 'Premium Display', value: 35, color: '#8b5cf6' },
      { name: 'Meta', value: 20, color: '#3b82f6' },
    ],
  },

  // =====================
  // LEXUS — UX
  // =====================
  {
    id: 'l-ux-1',
    title: 'UX City Explorer',
    brand: 'Lexus',
    type: 'Brand',
    date: 'Jun – Sep 2025',
    year: '2025',
    market: 'BiH',
    model: 'UX',
    metrics: generateMetrics(170000, 'Brand'),
    channels: [
      { name: 'Instagram', value: 50, color: '#ec4899' },
      { name: 'YouTube', value: 30, color: '#ef4444' },
      { name: 'Display', value: 20, color: '#f59e0b' },
    ],
  },
  {
    id: 'l-ux-2',
    title: 'UX Lifestyle Campaign',
    brand: 'Lexus',
    type: 'Brand',
    date: 'Jan – Mar 2024',
    year: '2024',
    market: 'SLO',
    model: 'UX',
    metrics: generateMetrics(160000, 'Brand'),
    channels: [
      { name: 'Meta', value: 45, color: '#3b82f6' },
      { name: 'YouTube', value: 35, color: '#ef4444' },
      { name: 'Display', value: 20, color: '#f59e0b' },
    ],
  },

  // =====================
  // LEXUS — UX EV
  // =====================
  {
    id: 'l-uxev-1',
    title: 'UX EV Range Promo',
    brand: 'Lexus',
    type: 'Tactical',
    date: 'Jul – Oct 2024',
    year: '2024',
    market: 'SLO',
    model: 'UX EV',
    metrics: generateMetrics(130000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 60, color: '#10b981' },
      { name: 'Meta', value: 40, color: '#3b82f6' },
    ],
  },
  {
    id: 'l-uxev-2',
    title: 'UX EV Green Drive',
    brand: 'Lexus',
    type: 'Brand',
    date: 'Feb – May 2025',
    year: '2025',
    market: 'SLO',
    model: 'UX EV',
    metrics: generateMetrics(190000, 'Brand'),
    channels: [
      { name: 'YouTube', value: 50, color: '#ef4444' },
      { name: 'Meta', value: 30, color: '#3b82f6' },
      { name: 'Premium Display', value: 20, color: '#8b5cf6' },
    ],
  },

  // =====================
  // LEXUS — NX
  // =====================
  {
    id: 'l-nx-1',
    title: 'NX Hybrid Performance',
    brand: 'Lexus',
    type: 'Brand',
    date: 'Mar – Jun 2024',
    year: '2024',
    market: 'CRO',
    model: 'NX',
    metrics: generateMetrics(220000, 'Brand'),
    channels: [
      { name: 'LinkedIn', value: 35, color: '#0ea5e9' },
      { name: 'YouTube', value: 35, color: '#ef4444' },
      { name: 'Meta', value: 30, color: '#3b82f6' },
    ],
  },
  {
    id: 'l-nx-2',
    title: 'NX Autumn Tactical',
    brand: 'Lexus',
    type: 'Tactical',
    date: 'Sep – Nov 2024',
    year: '2024',
    market: 'SLO',
    model: 'NX',
    metrics: generateMetrics(105000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 55, color: '#10b981' },
      { name: 'Meta', value: 35, color: '#3b82f6' },
      { name: 'Email', value: 10, color: '#8b5cf6' },
    ],
  },
  {
    id: 'l-nx-3',
    title: 'NX Spring Offer',
    brand: 'Lexus',
    type: 'Tactical',
    date: 'Jan – Apr 2026',
    year: '2026',
    market: 'SLO',
    model: 'NX',
    metrics: generateMetrics(160000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 55, color: '#10b981' },
      { name: 'Meta', value: 35, color: '#3b82f6' },
      { name: 'Email', value: 10, color: '#8b5cf6' },
    ],
  },
  {
    id: 'l-nx-4',
    title: 'NX Executive Drive',
    brand: 'Lexus',
    type: 'Brand',
    date: 'Jun – Aug 2025',
    year: '2025',
    market: 'RS',
    model: 'NX',
    metrics: generateMetrics(230000, 'Brand'),
    channels: [
      { name: 'LinkedIn', value: 40, color: '#0ea5e9' },
      { name: 'YouTube', value: 35, color: '#ef4444' },
      { name: 'Premium Display', value: 25, color: '#8b5cf6' },
    ],
  },

  // =====================
  // LEXUS — RZ
  // =====================
  {
    id: 'l-rz-1',
    title: 'RZ Electric Future',
    brand: 'Lexus',
    type: 'Brand',
    date: 'Jan – Apr 2025',
    year: '2025',
    market: 'SLO',
    model: 'RZ',
    metrics: generateMetrics(280000, 'Brand'),
    channels: [
      { name: 'YouTube', value: 50, color: '#ef4444' },
      { name: 'Premium Display', value: 30, color: '#8b5cf6' },
      { name: 'Meta', value: 20, color: '#3b82f6' },
    ],
  },
  {
    id: 'l-rz-2',
    title: 'RZ Test Drive Event',
    brand: 'Lexus',
    type: 'Tactical',
    date: 'May – Jul 2025',
    year: '2025',
    market: 'CRO',
    model: 'RZ',
    metrics: generateMetrics(95000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 50, color: '#10b981' },
      { name: 'Meta', value: 40, color: '#3b82f6' },
      { name: 'Email', value: 10, color: '#8b5cf6' },
    ],
  },

  // =====================
  // LEXUS — RX
  // =====================
  {
    id: 'l-rx-1',
    title: 'RX Luxury Event',
    brand: 'Lexus',
    type: 'Tactical',
    date: 'Oct – Dec 2024',
    year: '2024',
    market: 'RS',
    model: 'RX',
    metrics: generateMetrics(180000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 40, color: '#10b981' },
      { name: 'LinkedIn', value: 40, color: '#0ea5e9' },
      { name: 'Email', value: 20, color: '#8b5cf6' },
    ],
  },
  {
    id: 'l-rx-2',
    title: 'RX Premium Awareness',
    brand: 'Lexus',
    type: 'Brand',
    date: 'Feb – May 2024',
    year: '2024',
    market: 'SLO',
    model: 'RX',
    metrics: generateMetrics(240000, 'Brand'),
    channels: [
      { name: 'YouTube', value: 45, color: '#ef4444' },
      { name: 'LinkedIn', value: 30, color: '#0ea5e9' },
      { name: 'Premium Display', value: 25, color: '#8b5cf6' },
    ],
  },
  {
    id: 'l-rx-3',
    title: 'RX Year-End Offer',
    brand: 'Lexus',
    type: 'Tactical',
    date: 'Nov – Dec 2025',
    year: '2025',
    market: 'SLO',
    model: 'RX',
    metrics: generateMetrics(150000, 'Tactical'),
    channels: [
      { name: 'Google Ads', value: 50, color: '#10b981' },
      { name: 'Meta', value: 30, color: '#3b82f6' },
      { name: 'LinkedIn', value: 20, color: '#0ea5e9' },
    ],
  },
]
